import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import ProductsPaginationBar from "../components/ProductsPaginationBar";
import PasteListFlowHeader from "../components/PasteListFlowHeader";
import { getTotalPages } from "../utils/paginationScroll";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { clearActowizRequest, fetchActowizFeatures, getFeaturesRequestKey } from "../store/actowiz/actowizSlice";
import { fetchCurrentUser, updateBasketComparisonUsage } from "../store/auth/authSlice";
import { apiRequest } from "../api/client";
import { rememberGroceryProductView } from "../utils/groceryProductView";
import {
  calculateBasketTotals,
  expandPasteLines,
  fetchFeaturesProductsPage,
  loadPasteListFlow,
  savePasteListFlow,
  toQueryListParam,
} from "../utils/pasteListFlow";
import { PRICE_MODE_OPTIONS } from "../utils/catalogFilters";

const PAGE_SIZE = 40;

function resolvePasteListPriceMode(value) {
  return value === "offer_loyalty" ? "offer_loyalty" : "base";
}

function toCount(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function getBasketComparisonUsage(profile) {
  if (!profile || typeof profile !== "object") return null;

  const source =
    profile.basket_comparison ||
    profile.basket_comparisons ||
    profile.comparison_usage ||
    profile;

  const unlimited = source.unlimited === true;
  let used = toCount(source.used);
  let remaining = toCount(source.remaining);
  let monthlyLimit = toCount(source.monthly_limit ?? source.monthlyLimit);

  if (remaining == null && monthlyLimit != null && used != null) {
    remaining = Math.max(0, monthlyLimit - used);
  }
  if (monthlyLimit == null && used != null && remaining != null) {
    monthlyLimit = used + remaining;
  }

  return { unlimited, used, remaining, monthlyLimit };
}

function formatComparisonCount(value) {
  return value == null ? "0" : String(value);
}

function PasteListReview({ onOpenAuth, onOpenMenu }) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const token = useAppSelector((s) => s.auth.token);
  const user = useAppSelector((s) => s.auth.user);
  const authStatus = useAppSelector((s) => s.auth.status);
  const normalizedToken = typeof token === "string" ? token.trim() : token;
  const isLoggedIn =
    Boolean(normalizedToken) &&
    normalizedToken !== "null" &&
    normalizedToken !== "undefined";
  const [lines, setLines] = useState([]);
  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [listPickerOpen, setListPickerOpen] = useState(false);
  const [listPickerOptions, setListPickerOptions] = useState([]);
  const [listPickerResolver, setListPickerResolver] = useState(null);
  const [basketTotals, setBasketTotals] = useState(null);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [compareConfirmOpen, setCompareConfirmOpen] = useState(false);
  const [compareSubmitting, setCompareSubmitting] = useState(false);
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const [limitModalMessage, setLimitModalMessage] = useState("");
  const [priceMode, setPriceMode] = useState(() =>
    resolvePasteListPriceMode(loadPasteListFlow()?.priceMode)
  );
  const topRef = useRef(null);
  const pageScrollTokenRef = useRef(0);
  const totalCountRef = useRef(0);
  const hasLoadedProductsRef = useRef(false);
  const hasNextRef = useRef(false);
  const prevPriceModeRef = useRef(priceMode);
  const isOfferMode = priceMode === "offer_loyalty";

  const handlePriceModeChange = useCallback((mode) => {
    const nextMode = resolvePasteListPriceMode(mode);
    setPriceMode(nextMode);
    const flow = loadPasteListFlow();
    if (flow) {
      savePasteListFlow({
        ...flow,
        priceMode: nextMode,
        reviewPage: 1,
      });
    }
  }, []);

  const queryList = useMemo(() => {
    if (!lines.length) return "";
    return toQueryListParam(lines.join("\n"));
  }, [lines]);

  const getRequestKey = useCallback(
    (targetPage) => {
      const start = (targetPage - 1) * PAGE_SIZE;
      return getFeaturesRequestKey({
        query_list: String(queryList || "").trim().toLowerCase(),
        offer: isOfferMode,
        start,
        offset: PAGE_SIZE,
      });
    },
    [queryList, isOfferMode]
  );

  const totalProductsCount =
    typeof totalProducts === "number" && totalProducts > 0 ? totalProducts : totalCountRef.current;

  const hasNextPage = products.length >= PAGE_SIZE;
  useEffect(() => {
    if (!isPageLoading) {
      hasNextRef.current = hasNextPage;
    }
  }, [isPageLoading, hasNextPage]);
  const hasNext = isPageLoading ? hasNextRef.current : hasNextPage;
  const totalPages = useMemo(
    () => getTotalPages(totalProductsCount, PAGE_SIZE, page, hasNext),
    [totalProductsCount, page, hasNext]
  );

  const showPagination = totalProductsCount > 0 || page > 1;
  const showProductGrid = products.length > 0;
  const showBasketSummary = !isOfferMode && (showPagination || showProductGrid);
  const profileLoading = isLoggedIn && !user && authStatus === "loading";
  const canStartComparison = user?.allowed === true && user?.can_compare === true;
  const comparisonLimitMessage =
    "Monthly basket comparison limit reached. Try again next month or upgrade your plan.";
  const isBasketSummaryDisabled = isLoggedIn && Boolean(user) && !canStartComparison;
  const comparisonUsage = useMemo(() => getBasketComparisonUsage(user), [user]);

  const scrollToTop = useCallback(() => {
    const scrollWindowToTop = () => {
      window.scrollTo(0, 0);
      const scrollingElement = document.scrollingElement;
      if (scrollingElement) scrollingElement.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    scrollWindowToTop();
    topRef.current?.scrollIntoView({ block: "start", behavior: "auto" });
    requestAnimationFrame(scrollWindowToTop);
  }, []);

  const scheduleScrollToTop = useCallback(() => {
    const token = pageScrollTokenRef.current + 1;
    pageScrollTokenRef.current = token;

    const run = () => {
      if (pageScrollTokenRef.current !== token) return;
      scrollToTop();
    };

    run();
    window.setTimeout(run, 0);
    window.setTimeout(run, 120);
    window.setTimeout(run, 320);
  }, [scrollToTop]);

  const goToPage = useCallback(
    (targetPage) => {
      dispatch(clearActowizRequest(getRequestKey(targetPage)));
      setPage(targetPage);
      const flow = loadPasteListFlow();
      if (flow) {
        savePasteListFlow({ ...flow, reviewPage: targetPage, priceMode });
      }
    },
    [dispatch, getRequestKey, priceMode]
  );

  useEffect(() => {
    if (prevPriceModeRef.current === priceMode) return;
    prevPriceModeRef.current = priceMode;
    setPage(1);
    hasLoadedProductsRef.current = false;
    setProducts([]);
    setTotalProducts(0);
    totalCountRef.current = 0;
  }, [priceMode]);

  useEffect(() => {
    if (page <= 1 || status !== "succeeded" || isPageLoading) return;
    scheduleScrollToTop();
  }, [page, status, isPageLoading, scheduleScrollToTop]);

  const renderPaginationSection = (className = "") => {
    if (!showPagination && !showBasketSummary) return null;
    return (
      <div
        className={`flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between ${className}`.trim()}
      >
        {showPagination ? (
          <ProductsPaginationBar
            page={page}
            totalPages={totalPages}
            totalProducts={totalProductsCount}
            isLoading={isPageLoading}
            onPageChange={goToPage}
            className="!mt-0 w-full justify-center sm:w-auto sm:justify-start"
          />
        ) : (
          <div />
        )}
        {showBasketSummary && (
          <div className="flex shrink-0 flex-col items-center gap-1.5 self-center sm:items-end sm:self-auto">
            <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-end">
              <div
                className={`group relative${
                  isBasketSummaryDisabled ? " cursor-not-allowed" : ""
                }`}
              >
                <button
                  type="button"
                  onClick={handleContinue}
                  disabled={isBasketSummaryDisabled || profileLoading}
                  aria-label={
                    isBasketSummaryDisabled ? `See basket summary — ${comparisonLimitMessage}` : "See basket summary"
                  }
                  className="rounded-full bg-[#7352C7] px-6 py-2 text-[14px] font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-60 sm:text-[16px]"
                >
                  See basket summary
                </button>
                {isBasketSummaryDisabled && (
                  <span
                    role="tooltip"
                    className="pointer-events-none absolute bottom-full right-0 z-50 mb-2 w-max max-w-[18rem] rounded-lg border border-amber-200 bg-white px-3 py-2 text-left text-[13px] font-medium leading-snug text-amber-900 opacity-0 shadow-lg transition-opacity group-hover:opacity-100 sm:text-[14px]"
                  >
                    {comparisonLimitMessage}
                  </span>
                )}
              </div>
              {isBasketSummaryDisabled && (
                <button
                  type="button"
                  onClick={() => navigate("/subscription")}
                  className="rounded-full border border-[#7352C7] bg-white px-5 py-2 text-[14px] font-semibold text-[#7352C7] shadow-sm sm:text-[16px]"
                >
                  Upgrade plan
                </button>
              )}
            </div>
            {isLoggedIn && user && comparisonUsage && !comparisonUsage.unlimited && (
              <p
                className={`text-center text-[12px] font-medium sm:text-right sm:text-[13px] ${
                  isBasketSummaryDisabled ? "text-amber-800" : "text-[#6E7B86]"
                }`}
              >
                {formatComparisonCount(comparisonUsage.remaining)} comparison
                {comparisonUsage.remaining === 1 ? "" : "s"} available
              </p>
            )}
            {isLoggedIn && user && comparisonUsage?.unlimited && (
              <p className="text-center text-[12px] font-medium text-[#6E7B86] sm:text-right sm:text-[13px]">
                Unlimited comparisons available
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  const closeListPicker = useCallback(() => {
    listPickerResolver?.(null);
    setListPickerOpen(false);
    setListPickerOptions([]);
    setListPickerResolver(null);
  }, [listPickerResolver]);

  const chooseList = useCallback((lists) => {
    return new Promise((resolve) => {
      setListPickerOptions(lists);
      setListPickerResolver(() => resolve);
      setListPickerOpen(true);
    });
  }, []);

  useEffect(() => {
    if (!listPickerOpen) return undefined;
    const onEsc = (e) => {
      if (e.key === "Escape") closeListPicker();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [listPickerOpen, closeListPicker]);

  useEffect(() => {
    const flow = loadPasteListFlow();
    if (!flow?.lines?.length) {
      navigate("/paste-list", { replace: true });
      return;
    }
    const restoredMode = resolvePasteListPriceMode(flow.priceMode);
    setPriceMode(restoredMode);
    prevPriceModeRef.current = restoredMode;
    setLines(expandPasteLines(flow.rawText || flow.lines.join("\n")));
    if (typeof flow.totalCount === "number" && flow.totalCount > 0) {
      totalCountRef.current = flow.totalCount;
      setTotalProducts(flow.totalCount);
    }
    if (Array.isArray(flow.products) && flow.products.length) {
      setProducts(flow.products);
      hasLoadedProductsRef.current = true;
      setStatus("succeeded");
    }
    const restoredPage =
      typeof flow.reviewPage === "number" && flow.reviewPage > 0 ? flow.reviewPage : 1;
    setPage(restoredPage);
  }, [navigate]);

  useEffect(() => {
    if (!isLoggedIn || user || authStatus === "loading") return;
    dispatch(fetchCurrentUser());
  }, [isLoggedIn, user, authStatus, dispatch]);

  useEffect(() => {
    if (!lines.length || !queryList) return undefined;

    const flow = loadPasteListFlow();
    if (!flow?.lines?.length) return undefined;

    let cancelled = false;
    (async () => {
      const isInitialLoad = !hasLoadedProductsRef.current;
      setIsPageLoading(true);
      if (isInitialLoad) {
        setStatus("loading");
      }
      setError("");
      try {
        const result = await fetchFeaturesProductsPage(dispatch, fetchActowizFeatures, {
          queryList,
          page,
          pageSize: PAGE_SIZE,
          offer: isOfferMode,
        });
        const items = result.items;
        const total = result.totalCount;
        const totals =
          result.basketTotals?.cheapest
            ? result.basketTotals
            : flow.basketTotals?.cheapest
            ? flow.basketTotals
            : null;

        if (cancelled) return;
        setProducts(items);
        hasLoadedProductsRef.current = items.length > 0;
        if (typeof total === "number" && total > 0) {
          setTotalProducts(total);
          totalCountRef.current = total;
        } else if (page === 1) {
          setTotalProducts(0);
          totalCountRef.current = 0;
        }
        setBasketTotals(totals);
        savePasteListFlow({
          ...flow,
          rawText: flow.rawText || (flow.lines || []).join("\n"),
          queryList,
          products: items,
          totalCount: totalCountRef.current || total,
          basketTotals: totals || flow.basketTotals,
          priceMode,
          reviewPage: page,
        });
        setStatus("succeeded");
      } catch (err) {
        if (cancelled) return;
        setError(typeof err === "string" ? err : err?.message || "Failed to load matches.");
        setStatus("failed");
      } finally {
        if (!cancelled) setIsPageLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, lines, page, queryList, isOfferMode, priceMode]);

  const proceedToSummary = () => {
    const flow = loadPasteListFlow();
    if (!flow) return;
    const totals =
      basketTotals?.cheapest
        ? basketTotals
        : flow.basketTotals?.cheapest
        ? flow.basketTotals
        : calculateBasketTotals(products);
    savePasteListFlow({
      ...flow,
      products,
      totalCount: totalProducts,
      basketTotals: totals,
      priceMode,
      reviewPage: page,
    });
    navigate("/paste-list/summary");
  };

  const openLimitModal = (message) => {
    setLimitModalMessage(
      message ||
        comparisonLimitMessage
    );
    setLimitModalOpen(true);
  };

  const handleContinue = async () => {
    if (!isLoggedIn) {
      onOpenAuth?.("login");
      return;
    }
    if (profileLoading) return;
    if (!user) {
      dispatch(fetchCurrentUser());
      return;
    }

    if (!canStartComparison) {
      openLimitModal(comparisonLimitMessage);
      return;
    }

    let latestProfile = user;
    try {
      latestProfile = await dispatch(fetchCurrentUser({ force: true })).unwrap();
    } catch {
      // Use cached profile if refresh fails.
    }

    const canCompareNow =
      latestProfile?.allowed === true && latestProfile?.can_compare === true;
    if (!canCompareNow) {
      openLimitModal(comparisonLimitMessage);
      return;
    }

    setCompareConfirmOpen(true);
  };

  const handleConfirmCompare = async () => {
    setCompareSubmitting(true);
    try {
      const compare = await apiRequest("api/basket_comparisons?compare=true", { method: "GET" });
      if (!compare.ok) {
        const message =
          compare.data?.errors?.[0] ||
          compare.data?.message ||
          "Could not start basket comparison.";
        if (compare.data) {
          dispatch(updateBasketComparisonUsage(compare.data));
        }
        setCompareConfirmOpen(false);
        openLimitModal(message);
        return;
      }

      dispatch(updateBasketComparisonUsage(compare.data));
      setCompareConfirmOpen(false);
      proceedToSummary();
    } finally {
      setCompareSubmitting(false);
    }
  };

  const handleAddToList = useCallback(
    async (product) => {
      const minimalRes = await apiRequest("api/grocery_lists_minimal", { method: "GET" });
      if (!minimalRes.ok) {
        const errors = Array.isArray(minimalRes.data?.errors) ? minimalRes.data.errors : null;
        throw new Error(errors?.join("\n") || minimalRes.data?.message || "Failed to load grocery lists.");
      }

      const lists = Array.isArray(minimalRes.data)
        ? minimalRes.data
        : Array.isArray(minimalRes.data?.data)
        ? minimalRes.data.data
        : [];

      if (!lists.length) throw new Error("No grocery list found. Please create a list first.");

      let selectedListId = null;
      if (lists.length === 1) {
        selectedListId = lists[0]?.id ?? lists[0]?.grocery_list_id ?? lists[0]?.list_id ?? null;
      } else {
        const selected = await chooseList(lists);
        if (!selected) {
          const cancelError = new Error("ADD_TO_LIST_CANCELLED");
          cancelError.code = "ADD_TO_LIST_CANCELLED";
          throw cancelError;
        }
        selectedListId = selected?.id ?? selected?.grocery_list_id ?? selected?.list_id ?? null;
      }
      if (!selectedListId) throw new Error("Unable to select grocery list.");

      const productId = product?.product_id ?? product?.pid ?? product?.sku ?? null;
      if (!productId) throw new Error("Invalid product.");

      const addRes = await apiRequest(`api/grocery_lists/${selectedListId}/grocery_products`, {
        method: "POST",
        body: {
          product_id: String(productId),
          product_name: product?.title || product?.product_name || product?.name || product?.sku || "",
          category: product?.category || product?.category_name || undefined,
          subcategory: product?.subcategory || product?.sub_category || product?.subCategory || undefined,
          platform:
            String(product?.platform || "")
              .trim()
              .toLowerCase()
              .replace("morrisons", "morrison")
              .replace("sainsburys", "sainsbury") || undefined,
          image_url: product?.images?.[0] || product?.image || product?.image_url || "",
          price: product?.price ?? null,
          weight: product?.packsize || product?.weight || "",
          quantity: 1,
        },
      });
      if (!addRes.ok) {
        const errors = Array.isArray(addRes.data?.errors) ? addRes.data.errors : null;
        throw new Error(errors?.join("\n") || addRes.data?.message || "Failed to add product to list.");
      }

      rememberGroceryProductView({
        listId: selectedListId,
        productId: String(productId),
        view: "compare",
      });
    },
    [chooseList]
  );

  return (
    <div ref={topRef} className="flex min-h-screen flex-col bg-[#F5F7FB] text-[#24303A]">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />
      <PasteListFlowHeader currentStep={2} />

      <main className="mx-auto w-full max-w-7xl flex-1 px-3 py-5 sm:px-4 sm:py-6 md:px-6">
        <div className="mb-5 flex min-w-0 items-start justify-between gap-4 rounded-2xl border border-[#E6EAF0] bg-white p-4 shadow-sm sm:items-center sm:p-5">
          <div className="min-w-0 flex-1">
            <h2 className="text-[20px] font-semibold text-[#24303A] sm:text-[22px]">Review Matches</h2>
            <p className="mt-1 text-[14px] text-[#6E7B86] sm:text-[16px]">
              Showing {products.length} of {totalProductsCount} products
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              const flow = loadPasteListFlow();
              if (flow) {
                savePasteListFlow({
                  ...flow,
                  rawText: flow.rawText || (flow.lines || []).join("\n"),
                });
              }
              navigate("/paste-list", { state: { fromEditList: true } });
            }}
            className="shrink-0 rounded-full border border-[#E6EAF0] bg-[#7352C7] px-5 py-1.5 text-[14px] font-semibold text-[#fff] shadow-sm sm:py-1.5 sm:text-[16px]"
          >
            Edit list
          </button>
        </div>

        <div className="mb-5">
          <h3 className="mb-2 text-[18px] font-semibold text-[#24303A]">Price Mode</h3>
          <div className="-mx-1 flex snap-x snap-mandatory flex-nowrap gap-2 overflow-x-auto overscroll-x-contain px-1 pb-1 touch-pan-x [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {PRICE_MODE_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => handlePriceModeChange(option.id)}
                className={[
                  "shrink-0 snap-start whitespace-nowrap rounded-full px-3 py-1.5 font-bold sm:px-4 text-[15px]",
                  priceMode === option.id
                    ? "border-2 border-[#7352C7] bg-[#7352C71A] text-[#7352C7]"
                    : "border-2 border-[#E6EAF0] bg-white text-[#6E7B86]",
                ].join(" ")}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {status === "loading" && products.length === 0 && (
          <div className="flex justify-center py-16">
            <span className="text-[16px] text-[#6E7B86]">Loading products...</span>
          </div>
        )}

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[14px] text-red-700 sm:text-[16px]">
            {error}
          </div>
        )}

        {renderPaginationSection("mb-6")}

        {showProductGrid && (
          <div
            className={`grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3${
              isPageLoading ? " pointer-events-none opacity-60" : ""
            }`}
          >
            {products.map((product, idx) => (
              <ProductCard
                key={`${product.pid || product.sku}-${idx}`}
                accent={["orange", "blue", "green", "purple"][idx % 4]}
                product={product}
                pricingMode={isOfferMode ? "Loyalty+Offer" : "Base"}
                onAddToList={handleAddToList}
                onClick={() => {
                  const productId = product.pid || product.sku;
                  if (!productId) return;
                  try {
                    window.sessionStorage.setItem(
                      `product-detail-cache:${productId}`,
                      JSON.stringify(product)
                    );
                  } catch {
                    // ignore cache write failures
                  }
                  navigate(`/product/${encodeURIComponent(productId)}`, {
                    state: {
                      product,
                      pricingMode: isOfferMode ? "Loyalty+Offer" : "Base",
                      offer: isOfferMode,
                    },
                  });
                }}
              />
            ))}
          </div>
        )}

        {!error && status !== "loading" && !isPageLoading && products.length === 0 && (
          <div className="flex justify-center py-16">
            <span className="text-[16px] text-slate-500">No products found.</span>
          </div>
        )}

        {showProductGrid && renderPaginationSection("mt-6")}
      </main>

      <Footer />

      {listPickerOpen && (
        <div className="fixed inset-0 z-[80]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close list selector"
            onClick={closeListPicker}
          />
          <div className="relative mx-auto flex min-h-screen max-w-3xl items-center justify-center px-4 py-8">
            <div className="w-full max-w-md rounded-2xl border border-[#E6EAF0] bg-white p-5 shadow-xl">
              <h3 className="text-base font-bold text-[#24303A]">Choose Grocery List</h3>
              <div className="mt-4 space-y-2">
                {listPickerOptions.map((list, index) => {
                  const listId = list?.id ?? list?.grocery_list_id ?? list?.list_id ?? "";
                  const listTitle = list?.title || `List ${index + 1}`;
                  return (
                    <button
                      key={`${listId}-${index}`}
                      type="button"
                      className="w-full rounded-xl border border-[#E6EAF0] bg-white px-3 py-2 text-left hover:bg-slate-50"
                      onClick={() => {
                        listPickerResolver?.(list);
                        setListPickerOpen(false);
                        setListPickerOptions([]);
                        setListPickerResolver(null);
                      }}
                    >
                      <p className="text-[16px] font-semibold text-slate-800">{listTitle}</p>
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={closeListPicker}
                  className="rounded-lg border border-[#E6EAF0] bg-white px-3 py-1.5 text-[16px] font-semibold text-slate-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {compareConfirmOpen && (
        <div className="fixed inset-0 z-[90]">
          <button
            type="button"
            aria-label="Close compare confirmation"
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              if (!compareSubmitting) setCompareConfirmOpen(false);
            }}
          />
          <div className="relative mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
            <div className="relative w-full max-w-md rounded-2xl border border-[#E6EAF0] bg-white p-6 shadow-xl">
              <h3 className="text-lg font-semibold text-slate-900">Compare basket?</h3>
              <p className="mt-2 text-[16px] text-slate-600">
                Are you sure you want to compare?
                {comparisonUsage?.unlimited ? null : (
                  <>
                    {" "}
                    This will use 1 of your monthly basket comparisons.
                  </>
                )}
              </p>
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  disabled={compareSubmitting}
                  onClick={() => setCompareConfirmOpen(false)}
                  className="flex-1 rounded-full border border-slate-300 bg-white px-4 py-2 text-[16px] font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  No
                </button>
                <button
                  type="button"
                  disabled={compareSubmitting}
                  onClick={handleConfirmCompare}
                  className="flex-1 rounded-full bg-[#7352C7] px-4 py-2 text-[16px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {compareSubmitting ? "Starting…" : "Yes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {limitModalOpen && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl border border-[#E6EAF0] bg-white p-6 text-center shadow-xl">
            <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full border-2 border-amber-400 text-amber-500">
              <span className="text-[20px] font-bold">!</span>
            </div>
            <h3 className="text-[20px] font-bold text-[#24303A] sm:text-[22px]">Comparison limit</h3>
            <p className="mt-2 text-[15px] leading-relaxed text-[#6E7B86] sm:text-[16px]">
              {limitModalMessage || comparisonLimitMessage}
            </p>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-center">
              <button
                type="button"
                onClick={() => setLimitModalOpen(false)}
                className="rounded-full border border-[#E6EAF0] bg-white px-6 py-2 text-[15px] font-semibold text-[#24303A] sm:text-[16px]"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  setLimitModalOpen(false);
                  navigate("/subscription");
                }}
                className="rounded-full bg-[#7352C7] px-6 py-2 text-[15px] font-semibold text-white shadow-sm sm:text-[16px]"
              >
                Upgrade plan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PasteListReview;
